import { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/reviews/approved`;

function Stars({ value, className = 'w-3.5 h-3.5' }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${className} ${i <= value
          ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-600'}`} />
      ))}
    </span>
  );
}

/** Approved author reviews, rendered on the landing page. */
export default function ReviewsSection() {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });

  useEffect(() => {
    axios.get(API).then(({ data: d }) => setData(d)).catch(() => {});
  }, []);

  return (
    <section className="py-16 border-t border-slate-200 dark:border-white/10" data-testid="landing-reviews">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
              Reviewed by the people who use it
            </p>
            <h2 className="text-base md:text-lg font-semibold">
              What authors say about InkPhD
            </h2>
          </div>
          <div className="flex items-center gap-2" data-testid="landing-reviews-average">
            <Stars value={Math.round(data.average)} className="w-4 h-4" />
            <span className="text-sm font-semibold">{data.average.toFixed(1)}</span>
            <span className="text-xs text-slate-500">
              from {data.count} review{data.count === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {data.reviews.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="landing-reviews-empty">
            The first reviews are being moderated. Used InkPhD?{' '}
            <a href="/thesis/profile" className="underline">Leave yours</a>.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.reviews.slice(0, 6).map((r, i) => (
            <figure key={i} data-testid={`landing-review-${i}`}
              className="rounded-xl border border-slate-200 dark:border-white/10 p-5 bg-white/60
                dark:bg-white/[0.03]">
              <Stars value={r.rating} />
              <blockquote className="text-[13.5px] leading-relaxed mt-3 mb-4">“{r.text}”</blockquote>
              <figcaption className="text-[12px] text-slate-500">
                <span className="font-semibold text-foreground">{r.display_name}</span>
                {r.role ? ` · ${r.role}` : ''}{r.country ? ` · ${r.country}` : ''}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export { Stars };
